import { buildApiUrl } from '../../services/apiConfig';

class ExploreApiService {
  async request(endpoint, options = {}) {
    const token = localStorage.getItem('auth_token') || 'dev-token';
    const timeout = options.timeout || 60000;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(buildApiUrl('NOVA_INGESTION', endpoint), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers,
        },
        signal: controller.signal,
        ...options,
      });
      clearTimeout(timer);
      if (!response.ok) {
        throw new Error(`API error ${response.status}: ${await response.text()}`);
      }
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return null;
      }
      return response.json();
    } catch (err) {
      clearTimeout(timer);
      throw err;
    }
  }

  async getSource(sourceId) {
    return this.request(`/sources/${sourceId}`);
  }

  async getSourceObjects(sourceId) {
    const resp = await this.request(`/sources/${sourceId}/objects`, {
      method: 'POST',
      timeout: 180000,
    });
    return resp.objects || [];
  }

  async getTablePreview(sourceId, objectPath, limit = 1000, sheetName) {
    const body = { object_path: objectPath, limit };
    if (sheetName) body.sheet_name = sheetName;
    return this.request(`/sources/${sourceId}/preview`, {
      method: 'POST',
      body: JSON.stringify(body),
      timeout: 60000,
    });
  }

  async deleteFile(filePath) {
    return this.request(`/upload/files/${encodeURIComponent(filePath)}`, { method: 'DELETE' });
  }

  async generateSynthetic(sourceId, objectPath, rows = 100) {
    return this.request('/synthetic/generate-from-db-new', {
      method: 'POST',
      body: JSON.stringify({ source_id: sourceId, object_path: objectPath, num_rows: rows }),
      timeout: 180000,
    });
  }

  async getSyntheticPreview(downloadId, rows = 10) {
    return this.request(`/synthetic/preview/${downloadId}?rows=${rows}`, { timeout: 30000 });
  }

  // LLM analysis
  async analyzeComprehensive(params) {
    return this.request('/llm/analyze/comprehensive', {
      method: 'POST',
      body: JSON.stringify(params),
      timeout: 180000,
    });
  }

  async getSavedAnalyses(params = {}) {
    const qs = new URLSearchParams();
    if (params.file_path) qs.append('file_path', params.file_path);
    if (params.source_id) qs.append('source_id', params.source_id);
    if (params.object_path) qs.append('object_path', params.object_path);
    const url = `/llm/analyze/saved${qs.toString() ? '?' + qs.toString() : ''}`;
    return this.request(url, { timeout: 30000 });
  }

  async saveAnalysis(data) {
    return this.request('/llm/analyze/save', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // AI descriptions
  async generateDescriptions(params) {
    return this.request('/llm/descriptions', {
      method: 'POST',
      body: JSON.stringify(params),
      timeout: 120000,
    });
  }

  async getSavedDescriptions(params = {}) {
    const qs = new URLSearchParams();
    if (params.file_path) qs.append('file_path', params.file_path);
    if (params.source_id) qs.append('source_id', params.source_id);
    if (params.object_path) qs.append('object_path', params.object_path);
    if (params.sheet_name) qs.append('sheet_name', params.sheet_name);
    const url = `/llm/descriptions/object${qs.toString() ? '?' + qs.toString() : ''}`;
    return this.request(url, { method: 'GET', timeout: 30000 });
  }

  // Chat
  async chat(params) {
    return this.request('/llm/chat', {
      method: 'POST',
      body: JSON.stringify(params),
      timeout: 90000,
    });
  }

  // Bulk metadata
  async bulkGenerateMetadata(params) {
    return this.request('/llm/metadata/bulk-generate', {
      method: 'POST',
      body: JSON.stringify(params),
      timeout: 30000,
    });
  }

  async getBulkMetadataStatus(jobId) {
    return this.request(`/llm/metadata/bulk-status/${jobId}`, { timeout: 10000 });
  }

  async checkTableMetadata(sourceId, tablePath) {
    try {
      const resp = await this.getSavedDescriptions({ source_id: sourceId, object_path: tablePath });
      return { has_metadata: !!(resp.success && resp.data) };
    } catch {
      return { has_metadata: false };
    }
  }

  // Source management
  async createSource(data) {
    return this.request('/sources', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async testConnection(sourceId) {
    return this.request(`/sources/${sourceId}/test`, {
      method: 'POST',
      timeout: 30000,
    });
  }

  async updateSource(sourceId, data) {
    return this.request(`/sources/${sourceId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSource(sourceId) {
    return this.request(`/sources/${sourceId}`, { method: 'DELETE' });
  }

  // Domain analysis
  async runDomainAnalysis(sourceId, params) {
    return this.request(`/sources/${sourceId}/domain-analysis`, {
      method: 'POST',
      body: JSON.stringify(params),
      timeout: 300000,
    });
  }

  async listDomainAnalysisRuns(sourceId, userId, limit = 20) {
    const qs = new URLSearchParams({ limit });
    if (userId) qs.append('user_id', userId);
    return this.request(`/sources/${sourceId}/domain-analysis/runs?${qs.toString()}`);
  }

  async getDomainAnalysisRun(sourceId, runId) {
    return this.request(`/sources/${sourceId}/domain-analysis/run/${runId}`);
  }

  async getCachedDomainAnalysis(sourceId, paths, userId) {
    const pathsJson = encodeURIComponent(JSON.stringify(paths));
    const qs = new URLSearchParams({ object_paths: pathsJson });
    if (userId) qs.append('user_id', userId);
    return this.request(`/sources/${sourceId}/domain-analysis?${qs.toString()}`);
  }

  async generateSubdomainSQL(sourceId, params) {
    return this.request(`/sources/${sourceId}/generate-subdomain-sql`, {
      method: 'POST',
      body: JSON.stringify(params),
      timeout: 120000,
    });
  }

  async validateSQL(sourceId, sqlStatement) {
    return this.request(`/sources/${sourceId}/validate-sql`, {
      method: 'POST',
      body: JSON.stringify({ sql_statement: sqlStatement }),
    });
  }

  async saveQueryContexts(sourceId, contexts, userId) {
    return this.request(`/sources/${sourceId}/query-context`, {
      method: 'POST',
      body: JSON.stringify({ contexts, user_id: userId }),
    });
  }
}

export default new ExploreApiService();
